import { useState } from "react";
import { FoundingBanner, money2, usePublicPlans } from "@/components/billing/Plans";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LegalLinks } from "@/pages/Legal";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { afterLoginPath, useAuth } from "@/context/AuthContext";
import { PublicLayout } from "@/components/brand/Brand";
import { Field } from "@/components/common";
import { AuthCard, GoogleButton } from "@/pages/SignIn";

export default function SignUp() {
  const { session, signIn } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (session && session.type === "adult") return <Navigate to={afterLoginPath(session)} replace />;

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    setErrors({ ...errors, [k]: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = "Tell us your name";
    if (!form.email.trim()) errs.email = "Enter your email";
    if (form.password.length < 8) errs.password = "Password must be at least 8 characters";
    setErrors(errs);
    setError("");
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", form);
      nav(afterLoginPath(signIn(data)), { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <AuthCard title="Create your account" subtitle="Then create your family hub or join one.">
        <form onSubmit={submit} className="space-y-4" noValidate data-testid="signup-form">
          <Field label="Your name" htmlFor="su-name" error={errors.name} errorTestid="signup-name-error">
            <input id="su-name" className="q-input" autoComplete="name" value={form.name} onChange={set("name")} aria-invalid={!!errors.name} data-testid="signup-name-input" />
          </Field>
          <Field label="Email" htmlFor="su-email" error={errors.email} errorTestid="signup-email-error">
            <input id="su-email" type="email" autoComplete="email" className="q-input" value={form.email} onChange={set("email")} aria-invalid={!!errors.email} data-testid="signup-email-input" />
          </Field>
          <Field label="Password" htmlFor="su-pass" error={errors.password} errorTestid="signup-password-error">
            <input id="su-pass" type="password" autoComplete="new-password" className="q-input" placeholder="8+ characters" value={form.password} onChange={set("password")} aria-invalid={!!errors.password} data-testid="signup-password-input" />
          </Field>
          {error && <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="signup-error">{error}</p>}
          <button className="q-btn q-btn-primary w-full" disabled={busy} data-testid="signup-submit-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Create account
          </button>
        </form>
        <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.2em] text-q-faint">
          <span className="h-px flex-1 bg-q-line" /> or <span className="h-px flex-1 bg-q-line" />
        </div>
        <GoogleButton />
        <p className="mt-4 text-xs text-q-faint">By creating an account you agree to our <LegalLinks testid="signup-legal" />.</p>
        <div className="mt-6" data-testid="signup-plans"><PublicPlansMini /></div>
        <p className="mt-6 text-sm text-q-muted">
          Already have an account? <Link to="/signin" className="text-q-accent hover:underline" data-testid="signup-to-signin-link">Sign in</Link>
        </p>
      </AuthCard>
    </PublicLayout>
  );
}

function PublicPlansMini() {
  const p = usePublicPlans();
  if (!p) return null;
  return (
    <div className="space-y-3">
      <FoundingBanner plans={p} testid="signup-founding-banner" />
      <ul className="grid gap-2 text-sm sm:grid-cols-3">
        {p.plans.map((x) => <li key={x.key} className={`rounded-xl border px-3 py-2 ${x.popular ? "border-[rgb(var(--glow-family))]" : "border-q-line"}`} data-testid={`signup-plan-${x.key}`}><b className="block text-q-text">{x.name}</b><span className="text-q-muted">{money2(x.month_cents)}/mo · {money2(x.year_cents).replace(".00", "")}/yr</span></li>)}
      </ul>
      <p className="text-xs text-q-faint">Start with a {p.trial_days}-day free trial on Family Plus. No card needed now.</p>
    </div>
  );
}
